'use client'
import { SanityImageSource } from '@sanity/image-url'
import Image from 'next/image'
import Link from 'next/link'
import { isUkSite } from '@/lib/config/site'
import { cn } from '@/lib/utils/utils'
import { urlFor } from '@/sanity/lib/image'
import type { SITE_SETTINGS_QUERY_RESULT } from '@/sanity/types'
import { Button } from '../../../common/button'
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from '../../../common/navigation-menu'
import CalendlyFlowPopup from '../../../features/Calendly/CalendlyFlowPopup'
import RegisterInterestForm from '../../../features/BlockContent/HeroSliderBanner/RegisterInterest'
import MastHeadMobile from './MastHeadMobile'

/**
 * Masthead navigation type - extracted from GROQ-generated types
 * This ensures consistency with the query result structure
 */
type MastheadNavigationType =
  NonNullable<SITE_SETTINGS_QUERY_RESULT>['mastheadNavigation']

export type MastHeadRendererModernProps = {
  logo: SanityImageSource & { altText?: string }
  mastheadNavigation: MastheadNavigationType
  careers?: boolean
}

function MastHeadModernDesktop({
  logo,
  mastheadNavigation,
  careers,
}: {
  logo: SanityImageSource & { altText?: string }
  mastheadNavigation: MastheadNavigationType
  careers?: boolean
}) {
  const { navigation, showContactUs, showCareers, showFamilyHub, showCalendlyFlow, contactForm } =
    mastheadNavigation

  return (
    <div className={cn('mx-auto flex h-full max-w-7xl gap-4 px-5')}>
      <Link aria-label="Home" href={careers ? '/careers' : '/'}>
        <Image
          className="w-22"
          src={urlFor(logo).width(600).height(600).url()}
          width={600}
          height={600}
          alt={logo.altText || 'logo'}
        />
      </Link>
      <div className={cn('flex w-full items-center justify-between')}>
        <NavigationMenu viewport={false} className={cn('z-30')}>
          <NavigationMenuList>
            {navigation?.map((item, index) => {
              // Use _id as primary key (always present from query), with stable fallback
              const itemId = (item as { _id?: string })._id
              const baseKey =
                itemId ||
                `nav-${item._type ?? 'item'}-${item.title ?? ''}-${index}`

              if (item._type === 'navigationItem') {
                return (
                  <NavigationMenuItem key={baseKey}>
                    <NavigationMenuLink
                      asChild
                      className={navigationMenuTriggerStyle()}
                    >
                      <Link href={item?.url || ''}>{item.title}</Link>
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                )
              }

              return (
                <NavigationMenuItem key={baseKey}>
                  <NavigationMenuTrigger title={item.title || ''}>
                    {item.link?.url ? (
                      <Link href={item.link?.url || ''}>{item.title}</Link>
                    ) : (
                      <>{item.title}</>
                    )}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div
                      className={cn(
                        'grid gap-2',
                        (item.items?.length ?? 0) > 4
                          ? 'grid-cols-2'
                          : 'grid-cols-1'
                      )}
                    >
                      {item.items?.map((subItem, subIndex) => {
                        // Use _id as primary key for sub-items (always present from query)
                        const subItemId = (subItem as { _id?: string })._id
                        const subKey =
                          subItemId ||
                          `sub-${baseKey}-${subItem.title ?? ''}-${subIndex}`
                        return (
                          <NavigationMenuLink
                            key={subKey}
                            className="hover:bg-pink-100 text-primary-blue"
                            asChild
                          >
                            <Link href={subItem.url || ''}>
                              {subItem.title}
                            </Link>
                          </NavigationMenuLink>
                        )
                      })}
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              )
            })}
          </NavigationMenuList>
        </NavigationMenu>
        <div className={cn('flex h-full flex-col justify-center gap-2')}>
          <div className="flex items-center gap-2 xl:gap-5">
            {showContactUs && contactForm && (
              <RegisterInterestForm
                formTitle={contactForm?.formTitle || ''}
                formDescription={contactForm?.formSubTitle || ''}
              >
                <Button
                  size={'sm'}
                  variant="inverse"
                  className="rounded-full font-bold"
                >
                  {contactForm?.ctaTitle || 'Get in touch'}
                </Button>
              </RegisterInterestForm>
            )}
            {showCalendlyFlow && !careers && (
              <CalendlyFlowPopup>
                <Button
                  size={'sm'}
                  variant="inverse"
                  className="rounded-full font-bold"
                >
                  Enquire today
                </Button>
              </CalendlyFlowPopup>
            )}
            {showFamilyHub && (
              <Link href="/the-family-hub/">
                <Button
                  size={'sm'}
                  variant="inverse"
                  className="rounded-full font-bold"
                >
                  The Family Hub
                </Button>
              </Link>
            )}
            {showCareers && (
              <>
                {isUkSite() && !careers ? (
                  <a href="https://www.nfamilycareers.com/">
                    <Button
                      size={'sm'}
                      variant="green"
                      className="rounded-full font-bold"
                    >
                      Careers at N
                    </Button>
                  </a>
                ) : (
                  <Link href={careers ? '/' : '/careers'}>
                    <Button
                      size={'sm'}
                      variant="green"
                      className="rounded-full font-bold"
                    >
                      {careers ? 'N Family' : 'Careers at N'}
                    </Button>
                  </Link>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default function MastHeadRendererModern({
  logo,
  mastheadNavigation,
  careers,
}: MastHeadRendererModernProps) {
  if (!logo || !mastheadNavigation) return null

  return (
    <>
      <div className="hidden xl:block">
        <MastHeadModernDesktop
          logo={logo}
          mastheadNavigation={mastheadNavigation}
          careers={careers}
        />
      </div>
      <div className="block xl:hidden">
        <MastHeadMobile
          logo={logo}
          mastheadNavigation={mastheadNavigation}
          careers={careers}
        />
      </div>
    </>
  )
}
